import React from 'react';
import { Shelf } from '../../../../lib/modelBuilder/entities/Shelf';
import { useTranslation } from 'react-i18next';
import {
  StyledTabularContainer,
  StyledTabularDataRow,
  StyledTabularTitle,
  StyledTabularValue,
} from '../../../../components/styles/StyledTabular';
import {
  DoorHandleType,
  Material,
  MINIMUM_LARGE_DOORS_HEIGHT,
  Position,
  PositionAngle,
  ShelfComposition,
} from '../../../../lib/modelBuilder/constants';
import { CustomPiece } from '../../../../lib/modelBuilder/pieces/CustomPiece';
import { Door } from '../../../../lib/modelBuilder/pieces/Door';
import { DoorHandle } from '../../../../lib/modelBuilder/pieces/DoorHandle';
import { Pane } from '../../../../lib/modelBuilder/pieces/Pane';
import { Rack } from '../../../../lib/modelBuilder/entities/Rack';
import DrawersDescription from './DrawersDescription';
import PlateDescription from './PlateDescription';

// import styled from 'styled-components';

interface Props {
  shelf: Shelf;
  materialName: any;
}

const ShelfDescription = ({ shelf, materialName }: Props) => {
  const { t } = useTranslation();

  const getDoorHandleType = (type: DoorHandleType): string => {
    switch (type) {
      case DoorHandleType.Knop:
        return t('Knop');
      case DoorHandleType.Button:
        return t('Button');
      case DoorHandleType.BigButton:
        return t('Big button');
      case DoorHandleType.Slat:
        return t('Slat');
      default:
        return 'None';
    }
  };

  const getDoorDescription = (position: PositionAngle) => {
    const door: CustomPiece<Door> | undefined = shelf.getDoor(position);
    if (door) {
      let slat = '';
      if (door.base.slatMaterial) {
        if (door.base.slatMaterial === Material.Aluminium)
          slat += ` (${t('slat')} ${t('Aluminium').toLowerCase()})`;
        else slat += ` (${t('slat')} ${t('Wood').toLowerCase()})`;
      }
      return `${materialName(door.material)} ${door.color.name}${slat}`;
    } else return t('None');
  };

  const getPaneDescription = (rack: Rack, position: Position) => {
    const pane: CustomPiece<Pane> | undefined = rack.getPane(position);
    if (pane) {
      return `${materialName(pane.material)} ${pane.color.name}`;
    } else return t('None');
  };

  const getHandleDescription = (position: PositionAngle) => {
    const door = shelf.getDoor(position);
    if (door && door.handle) {
      return `${getDoorHandleType((door.handle as DoorHandle).type)} ${
        (door.handle as DoorHandle).color?.name
      }`;
    } else return t('None');
  };

  const getISDescription = () => (
    <StyledTabularDataRow>
      <StyledTabularTitle>{t('All intermediates shelves')}</StyledTabularTitle>
      <StyledTabularValue>
        {shelf.intermediatesShelves
          .map(
            isEntry =>
              `${t('short.height')}: ${isEntry.height / 10}cm ${t(
                'short.depth',
              )}: ${isEntry.depth / 10}cm ${materialName(
                isEntry.interShelf.material,
              )} ${isEntry.interShelf.color.name}`,
          )
          .join(' / ')}
      </StyledTabularValue>
    </StyledTabularDataRow>
  );

  return (
    <div className="mb-5">
      <div className="py-2 text-lg">
        {t('Shelf')} {t('of')} {(shelf.height / 10).toFixed()} cm
      </div>
      <StyledTabularContainer>
        <DrawersDescription shelf={shelf} materialName={materialName} />

        {(shelf.composition === ShelfComposition.TwoDoors ||
          shelf.composition === ShelfComposition.FourDoors) && (
          <>
            <StyledTabularDataRow>
              <StyledTabularTitle>{t('Doors')}</StyledTabularTitle>
              <StyledTabularValue>{`${getDoorDescription(
                PositionAngle.FrontLeft,
              )}/${getDoorDescription(
                PositionAngle.FrontRight,
              )}`}</StyledTabularValue>
            </StyledTabularDataRow>
            {shelf.height < MINIMUM_LARGE_DOORS_HEIGHT && (
              <StyledTabularDataRow>
                <StyledTabularTitle>{t('Handles')}</StyledTabularTitle>
                <StyledTabularValue>{`${getHandleDescription(
                  PositionAngle.FrontLeft,
                )} / ${getHandleDescription(
                  PositionAngle.FrontRight,
                )}`}</StyledTabularValue>
              </StyledTabularDataRow>
            )}
          </>
        )}

        {shelf.composition === ShelfComposition.FourDoors && (
          <>
            <StyledTabularDataRow>
              <StyledTabularTitle>{t('Backdoors')}</StyledTabularTitle>
              <StyledTabularValue>{`${getDoorDescription(
                PositionAngle.BackLeft,
              )} / ${getDoorDescription(
                PositionAngle.BackRight,
              )}`}</StyledTabularValue>
            </StyledTabularDataRow>
            <StyledTabularDataRow>
              <StyledTabularTitle>{t('Handles')}</StyledTabularTitle>
              <StyledTabularValue>{`${getHandleDescription(
                PositionAngle.BackLeft,
              )} / ${getHandleDescription(
                PositionAngle.BackRight,
              )}`}</StyledTabularValue>
            </StyledTabularDataRow>
          </>
        )}

        {shelf.racks.length === 1 && (
          <>
            <StyledTabularDataRow>
              <StyledTabularTitle>{t('Left pane')}</StyledTabularTitle>
              <StyledTabularValue>{`${getPaneDescription(
                shelf.rack(0),
                Position.Left,
              )}`}</StyledTabularValue>
            </StyledTabularDataRow>
            <StyledTabularDataRow>
              <StyledTabularTitle>{t('Back pane')}</StyledTabularTitle>
              <StyledTabularValue>{`${getPaneDescription(
                shelf.rack(0),
                Position.Back,
              )}`}</StyledTabularValue>
            </StyledTabularDataRow>
            <StyledTabularDataRow>
              <StyledTabularTitle>{t('Right pane')}</StyledTabularTitle>
              <StyledTabularValue>{`${getPaneDescription(
                shelf.rack(0),
                Position.Right,
              )}`}</StyledTabularValue>
            </StyledTabularDataRow>
          </>
        )}

        {shelf.racks.length > 1 &&
          shelf.racks.map((rack: Rack) => (
            <>
              <StyledTabularDataRow>
                <StyledTabularTitle>
                  {t('Left pane')} {rack.level + 1}
                </StyledTabularTitle>
                <StyledTabularValue>{`${getPaneDescription(
                  rack,
                  Position.Left,
                )}`}</StyledTabularValue>
              </StyledTabularDataRow>
              <StyledTabularDataRow>
                <StyledTabularTitle>
                  {t('Back pane')} {rack.level + 1}
                </StyledTabularTitle>
                <StyledTabularValue>{`${getPaneDescription(
                  rack,
                  Position.Back,
                )}`}</StyledTabularValue>
              </StyledTabularDataRow>
              <StyledTabularDataRow>
                <StyledTabularTitle>
                  {t('Right pane')} {rack.level + 1}
                </StyledTabularTitle>
                <StyledTabularValue>{`${getPaneDescription(
                  rack,
                  Position.Right,
                )}`}</StyledTabularValue>
              </StyledTabularDataRow>
            </>
          ))}

        <PlateDescription
          title={t('Plate')}
          rack={shelf.rack(0)}
          materialName={materialName}
        />

        {shelf.intermediatesShelves &&
          shelf.intermediatesShelves.length > 0 &&
          getISDescription()}
      </StyledTabularContainer>
    </div>
  );
};

export default ShelfDescription;
